import { humanizeError } from "@/lib/errors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Clock,
  Cloud,
  Copy,
  CornerDownLeft,
  ExternalLink,
  Globe,
  History,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  RotateCcw,
  ServerCog,
  Search,
  Pencil,
  Trash2,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import * as RDialog from "@radix-ui/react-dialog";

import {
  addCustomDomain,
  APIError,
  deleteCustomDomain,
  deploy,
  getProjectSelection,
  listCustomDomains,
  listDeployments,
  listServers,
  readStorage,
  restartAgent,
  verifyCustomDomain,
  type CustomDomain,
  type CustomDomainRecord,
  type Deployment,
  type ProjectSelection,
  type Server,
} from "@/lib/api";
import { ServerToolPicker, droppedFromSelection, type PickerResult } from "@/components/ServerToolPicker";
import { IntegrationsPanel } from "@/components/playbook/IntegrationsPanel";
import { AddIntegrationDialog } from "@/components/playbook/AddIntegrationDialog";
import { decodeBase64Text, parseRequiresTools } from "@/lib/playbook";
import { recommendedRows, unmetCount } from "@/lib/recommendedIntegrations";
import { PLAYBOOK_FILE } from "@/components/playbook/useFileTree";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { CopyButton, ErrorState, Loading, Spinner } from "@/components/Page";
import { cn } from "@/lib/utils";
import { ProjectAccess } from "./ProjectAccess";
import { ProjectNotifications } from "./ProjectNotifications";
import { PolicyTab } from "@/components/PolicyTab";
import { ScheduledTab } from "@/components/ScheduledTab";
import { MissionsTab } from "@/components/MissionsTab";
import { BuiltinToolsCard } from "@/components/BuiltinToolsCard";
import { AgentChatPanel, type AgentChatHandle } from "@/components/AgentChatPanel";
import { AgentAvatar, AppearanceDialog } from "@/components/AgentAvatar";
import { ChatHistory } from "@/components/ChatHistory";
import { ALL_SECTIONS, SECTION_GROUPS, sectionFromHash, type IconType, type SectionId } from "@/components/agentSettings";
import { WorkspaceTab } from "@/components/StorageTab";

function errMsg(e: unknown, fallback: string) {
  return humanizeError(e, fallback);
}

function hostOf(d: Deployment): string {
  try {
    return new URL(d.url).hostname;
  } catch {
    return d.subdomain ?? "";
  }
}

const isLive = (s: string) => s === "active" || s === "verified";
const isSettling = (s: string) => s === "pending" || s === "verifying";
// Basic FQDN check for inline validation of the custom-domain field.
const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](-?[a-z0-9])*\.)+[a-z]{2,}$/i;

// statusMeta maps a custom-domain status to its badge presentation.
function statusMeta(status: string): { label: string; icon: LucideIcon; cls: string; spin?: boolean } {
  switch (status) {
    case "active":
    case "verified":
      return { label: status === "active" ? "Active" : "Verified", icon: CheckCircle2, cls: "border-success/40 bg-success/10 text-success" };
    case "verifying":
      return { label: "Verifying", icon: RefreshCw, cls: "border-warning/40 bg-warning/10 text-warning", spin: true };
    case "pending":
      return { label: "Pending DNS", icon: Clock, cls: "border-warning/40 bg-warning/10 text-warning" };
    case "degraded":
      return { label: "Needs attention", icon: AlertTriangle, cls: "border-warning/50 bg-warning/15 text-warning" };
    case "failed":
      return { label: "Failed", icon: XCircle, cls: "border-destructive/40 bg-destructive/10 text-destructive" };
    default:
      return { label: status || "Unknown", icon: Clock, cls: "border-border bg-muted text-muted-foreground" };
  }
}

function StatusBadge({ status }: { status: string }) {
  const m = statusMeta(status);
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={cn("gap-1", m.cls)}>
      <Icon className={cn("h-3 w-3", m.spin && "animate-spin")} />
      {m.label}
    </Badge>
  );
}

// useDeployment loads one agent (its Berth deployment) by project id. Shared by
// the chat page and the settings section pages so both render the same agent.
function useDeployment(projectId: string) {
  const [project, setProject] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr(null);
    return listDeployments()
      .then((deps) => {
        const p = deps.find((d) => d.berth_project_id === projectId) ?? null;
        setProject(p);
        if (!p) setLoadErr("Agent not found.");
      })
      .catch((e) => setLoadErr(errMsg(e, "Couldn’t load agent")))
      .finally(() => setLoading(false));
  }, [projectId]);
  useEffect(() => {
    void load();
  }, [load]);

  return { project, loading, loadErr, reload: () => void load() };
}

export function ProjectDetail() {
  const { projectId = "" } = useParams();
  const { project, loading, loadErr, reload } = useDeployment(projectId);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }
  if (loadErr || !project) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b px-4 py-3">
          <BackLink />
        </div>
        <div className="flex flex-1 items-center justify-center p-6">
          <ErrorState message={loadErr ?? "Agent not found."} onRetry={reload} />
        </div>
      </div>
    );
  }

  return <AgentWorkspace project={project} projectId={projectId} onReload={reload} />;
}

// AgentSettingsPage renders one settings section as a normal in-shell page (the
// app sidebar shows the agent nav). The section list, content and validation are
// the same SectionContent the old overlay used.
export function AgentSettingsPage() {
  const { projectId = "", section = "" } = useParams();
  const { project, loading, loadErr, reload } = useDeployment(projectId);
  const sec = ALL_SECTIONS.find((s) => s.id === section);

  // Legacy alias: the section id was renamed notifications → channels.
  if (section === "notifications") return <Navigate to={`/agents/${projectId}/settings/channels`} replace />;
  if (!sec) return <Navigate to={`/agents/${projectId}`} replace />;
  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loading />
      </div>
    );
  }
  if (loadErr || !project) {
    return <ErrorState message={loadErr ?? "Agent not found."} onRetry={reload} />;
  }
  // Workspace is a full-bleed editing surface (the Layout bleeds this route): no
  // page header/padding — it fills the content area like the chat.
  if (sec.id === "workspace") {
    return <SectionContent id={sec.id} project={project} projectId={projectId} />;
  }
  return (
    <div className="space-y-5">
      <div>
        <div className="truncate text-xs text-muted-foreground">{project.name ?? project.berth_project_id}</div>
        <h1 className="text-lg font-semibold tracking-tight">{sec.label}</h1>
        <p className="text-sm text-muted-foreground">{sec.desc}</p>
      </div>
      <SectionContent id={sec.id} project={project} projectId={projectId} />
    </div>
  );
}

// The agent's customization surfaces, grouped for the settings overlay + ⌘K.
// Chat is no longer a section — it's the page itself.
// Section config + the settings nav are shared with the form routes (see
// @/components/agentSettings) so the left nav is identical in both.

type AgentIntegrations = {
  servers: Server[];
  selection: ProjectSelection | null;
  needed: string[];
  unmet: number;
  loading: boolean;
  err: string | null;
  refresh: () => Promise<void>;
};

// useAgentIntegrations loads what this agent's playbook recommends (requires_tools
// from the stored PLAYBOOK.md) alongside the user's connected servers and the
// agent's current selection, so both the Integrations-tab badge and the
// recommended-integrations section read from one live source. It re-reads on
// refresh, so it tracks playbook edits and AI re-generations. Reading the
// playbook is host-side and does not wake the sandbox; a missing playbook ⇒ [].
function useAgentIntegrations(projectId: string): AgentIntegrations {
  const [servers, setServers] = useState<Server[]>([]);
  const [selection, setSelection] = useState<ProjectSelection | null>(null);
  const [needed, setNeeded] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [srv, sel, body] = await Promise.all([
        listServers(),
        getProjectSelection(projectId),
        readStorage(projectId, "playbook", PLAYBOOK_FILE)
          .then((f) => decodeBase64Text(f.content_b64))
          .catch(() => ""),
      ]);
      setServers(srv);
      setSelection(sel);
      setNeeded(parseRequiresTools(body));
      setErr(null);
    } catch (e) {
      setErr(errMsg(e, "Couldn't load integrations"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const unmet = useMemo(() => unmetCount(recommendedRows(needed, servers, selection)), [needed, servers, selection]);
  return { servers, selection, needed, unmet, loading, err, refresh };
}

// StatusDot is the agent's live-state pill in the top bar.
function StatusDot({ project }: { project: Deployment }) {
  const live = !!project.url;
  const cls = !live ? "bg-muted-foreground" : project.stale ? "bg-warning" : "bg-success";
  const label = !live ? "Not deployed" : project.stale ? "Update available" : "Live";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
      <span className={cn("h-1.5 w-1.5 rounded-full", cls)} />
      {label}
    </span>
  );
}

// AgentWorkspace is the chat-first agent page: the conversation fills the screen,
// and every customization surface (the old tabs) lives in a summoned full-screen
// Settings overlay + a ⌘K command palette. You "dig into" an agent deliberately
// instead of being met with a wall of tabs.
function AgentWorkspace({
  project,
  projectId,
  onReload,
}: {
  project: Deployment;
  projectId: string;
  onReload: () => void;
}) {
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [full, setFull] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const chatRef = useRef<AgentChatHandle>(null);
  const toast = useToast();
  const confirm = useConfirm();

  const agentName = project.name ?? project.berth_project_id;
  const mcpURL = project.url ? `${project.url}/mcp` : "";

  // Settings are routed pages now (the sidebar shows the sections). Preserve old
  // deep-links: /agents/<id>#policy → /agents/<id>/settings/policy.
  useEffect(() => {
    const s = sectionFromHash();
    if (s) navigate(`/agents/${projectId}/settings/${s}`, { replace: true });
  }, [navigate, projectId]);

  const newChat = useCallback(() => chatRef.current?.newChat(), []);

  const restart = useCallback(async () => {
    const ok = await confirm({
      title: "Restart agent?",
      description:
        "Reboots the sandbox onto the latest runtime. The URL, tokens, playbook and storage are preserved; an in-flight turn briefly pauses.",
      confirmText: "Restart",
    });
    if (!ok) return;
    setRestarting(true);
    try {
      await restartAgent(projectId);
      toast.success("Restarting the agent…");
      onReload();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't restart the agent"));
    } finally {
      setRestarting(false);
    }
  }, [projectId, confirm, toast, onReload]);

  // ⌘K toggles the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Esc exits full-screen (when no overlay is open — those handle their own Esc).
  useEffect(() => {
    if (!full) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !historyOpen && !paletteOpen) setFull(false);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [full, historyOpen, paletteOpen]);

  const copyURL = useCallback(() => {
    if (!mcpURL) return;
    void navigator.clipboard.writeText(mcpURL);
    toast.success("Copied the MCP URL.");
  }, [mcpURL, toast]);

  const paletteItems: PaletteItem[] = [
    { id: "new-chat", label: "New chat", hint: "Conversation", icon: Plus, run: newChat },
    ...(mcpURL
      ? [
          { id: "open-url", label: "Open MCP URL", hint: "External", icon: ExternalLink, run: () => window.open(mcpURL, "_blank", "noopener") },
          { id: "copy-url", label: "Copy MCP URL", hint: "Clipboard", icon: Copy, run: copyURL },
        ]
      : []),
    { id: "restart", label: "Restart agent", hint: "Runtime", icon: RotateCcw, run: () => void restart() },
    ...SECTION_GROUPS.flatMap((g) =>
      g.items.map((s) => ({
        id: `sec-${s.id}`,
        label: s.label,
        hint: g.group,
        icon: s.icon,
        run: () => navigate(`/agents/${projectId}/settings/${s.id}`),
      })),
    ),
  ];

  return (
    // Full-screen just swaps this wrapper's class (fixed inset-0 covers the
    // sidebar + page chrome); the chat panel stays mounted, so the stream and the
    // conversation continue uninterrupted.
    <div className={full ? "fixed inset-0 z-50 flex flex-col bg-background" : "flex h-full flex-col"}>
      {/* Top bar */}
      <header className="flex items-center gap-3 border-b px-3 py-2.5">
        <button
          type="button"
          onClick={() => setAppearanceOpen(true)}
          className="group relative shrink-0 rounded-full transition-transform hover:scale-105"
          title="Customize agent"
          aria-label="Customize agent emoji and color"
        >
          <AgentAvatar name={agentName} emoji={project.emoji} color={project.color} size="sm" />
          <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-background bg-muted text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
            <Pencil className="h-2 w-2" />
          </span>
        </button>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold">{agentName}</span>
            <StatusDot project={project} />
          </div>
          {project.subdomain && (
            <span className="block truncate font-mono text-xs text-muted-foreground">{project.subdomain}</span>
          )}
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={() => setPaletteOpen(true)}
            className="hidden items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:flex"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Search</span>
            <kbd className="rounded border bg-muted px-1 font-sans text-[10px] leading-4">⌘K</kbd>
          </button>
          <Button variant="ghost" size="sm" onClick={newChat}>
            <Plus className="h-4 w-4" /> New chat
          </Button>
          <Button variant="ghost" size="icon" aria-label="Chat history" onClick={() => setHistoryOpen(true)}>
            <History className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={full ? "Exit full screen" : "Full screen"}
            onClick={() => setFull((v) => !v)}
          >
            {full ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Agent actions">
                {restarting ? <Spinner /> : <MoreHorizontal className="h-4 w-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {mcpURL && (
                <DropdownMenuItem onSelect={() => window.open(mcpURL, "_blank", "noopener")}>
                  <ExternalLink /> Open MCP URL
                </DropdownMenuItem>
              )}
              {mcpURL && (
                <DropdownMenuItem onSelect={copyURL}>
                  <Copy /> Copy MCP URL
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => void restart()} disabled={restarting}>
                <RotateCcw /> Restart agent
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* The conversation — the page */}
      <div className="min-h-0 flex-1">
        <AgentChatPanel ref={chatRef} projectId={projectId} agentName={agentName} />
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={paletteItems} />
      <AppearanceDialog
        key={`${project.emoji ?? ""}-${project.color ?? ""}`}
        open={appearanceOpen}
        onOpenChange={setAppearanceOpen}
        projectId={projectId}
        name={agentName}
        emoji={project.emoji}
        color={project.color}
        onSaved={onReload}
      />
      <ChatHistory
        projectId={projectId}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSelect={(c, r) => chatRef.current?.loadConversation(c, r)}
        onNewChat={() => chatRef.current?.newChat()}
      />
    </div>
  );
}

function SectionContent({
  id,
  project,
  projectId,
}: {
  id: SectionId;
  project: Deployment;
  projectId: string;
}) {
  switch (id) {
    case "general":
      return <GeneralTab project={project} />;
    case "tools":
      return <ToolsCard project={project} projectId={projectId} />;
    case "builtin":
      return <BuiltinToolsCard projectId={projectId} />;
    case "workspace":
      return (
        <WorkspaceTab
          projectId={projectId}
          agentName={project.name ?? project.berth_project_id}
          subdomain={subdomainOfDeployment(project)}
        />
      );
    case "domains":
      return <CustomDomainsCard projectId={projectId} target={hostOf(project)} />;
    case "policy":
      return <PolicyTab projectId={projectId} />;
    case "scheduled":
      return <ScheduledTab projectId={projectId} />;
    case "missions":
      return <MissionsTab projectId={projectId} />;
    case "channels":
      return <ProjectNotifications projectId={projectId} />;
    case "access":
      return <ProjectAccess projectId={projectId} />;
  }
}

type PaletteItem = { id: string; label: string; hint?: string; icon: IconType; run: () => void };

// CommandPalette is the ⌘K launcher: fuzzy-jump to any settings surface or quick
// action, with arrow-key navigation.
function CommandPalette({ open, onClose, items }: { open: boolean; onClose: () => void; items: PaletteItem[] }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? items.filter((i) => i.label.toLowerCase().includes(s) || (i.hint ?? "").toLowerCase().includes(s)) : items;
  }, [q, items]);

  useEffect(() => {
    setActive(0);
  }, [q, open]);
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else setQ("");
  }, [open]);

  const run = (it: PaletteItem) => {
    onClose();
    it.run();
  };

  return (
    <RDialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" />
        <RDialog.Content className="fixed left-1/2 top-[14%] z-[60] w-[min(92vw,560px)] -translate-x-1/2 overflow-hidden rounded-2xl border bg-popover text-popover-foreground shadow-2xl outline-none">
          <RDialog.Title className="sr-only">Command palette</RDialog.Title>
          <RDialog.Description className="sr-only">Jump to a setting or run a quick action.</RDialog.Description>
          <div className="flex items-center gap-2 border-b px-4">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActive((a) => Math.min(a + 1, filtered.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((a) => Math.max(a - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  const it = filtered[active];
                  if (it) run(it);
                }
              }}
              placeholder="Search settings and actions…"
              className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">esc</kbd>
          </div>
          <div className="scrollbar-thin max-h-[50vh] overflow-auto p-1.5">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">No matches</div>
            ) : (
              filtered.map((it, i) => {
                const Icon = it.icon;
                const on = i === active;
                return (
                  <button
                    key={it.id}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => run(it)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                      on ? "bg-primary/10" : "hover:bg-accent",
                    )}
                  >
                    <Icon className={cn("h-4 w-4 shrink-0", on ? "text-primary" : "text-muted-foreground")} />
                    <span className="flex-1 truncate">{it.label}</span>
                    {it.hint && <span className="text-xs text-muted-foreground">{it.hint}</span>}
                    {on && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />}
                  </button>
                );
              })
            )}
          </div>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

function GeneralTab({ project }: { project: Deployment }) {
  const mcpURL = project.url ? `${project.url}/mcp` : "";
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">MCP endpoint</CardTitle>
          <CardDescription>The default URL your clients connect to.</CardDescription>
        </CardHeader>
        <CardContent>
          {mcpURL ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm">
              <span className="truncate">{mcpURL}</span>
              <CopyButton value={mcpURL} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Not deployed yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <Detail label="Subdomain" value={project.subdomain ?? "—"} mono />
            <Detail label="Created" value={new Date(project.created_at).toLocaleString()} />
            <Detail
              label="Status"
              value={project.stale ? "Update available" : "Up to date"}
            />
            <Detail label="Project ID" value={project.berth_project_id} mono />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={cn("truncate", mono && "font-mono text-xs")}>{value}</dd>
    </div>
  );
}

// subdomainOfDeployment extracts the agent’s subdomain for the redeploy
// call (prefer the stored field; fall back to parsing the URL host).
function subdomainOfDeployment(d: Deployment): string {
  if (d.subdomain) return d.subdomain;
  try {
    return new URL(d.url).hostname.split(".")[0] ?? "";
  } catch {
    return "";
  }
}

// ToolsCard is the agent's Integrations tab: surface the integrations the
// playbook recommends (Connect/Attach the missing ones), then fine-tune which
// connected servers and tools the agent exposes. Save and Attach both redeploy
// (the URL and access tokens stay the same; the running sandbox is replaced).
function ToolsCard({
  project,
  projectId,
}: {
  project: Deployment;
  projectId: string;
}) {
  const integrations = useAgentIntegrations(projectId);
  const { servers, selection, needed, loading, err, refresh } = integrations;
  const connected = useMemo(() => servers.filter((s) => s.connected), [servers]);
  const [picker, setPicker] = useState<PickerResult>({ selection: undefined, isEmpty: false });
  const [saving, setSaving] = useState(false);
  const [attaching, setAttaching] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const toast = useToast();

  const dropped = droppedFromSelection(selection, picker.selection, connected);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await deploy(project.name ?? projectId, subdomainOfDeployment(project), projectId, picker.selection);
      toast.success("Tools updated — redeploying with the new selection.");
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  }, [project, projectId, picker.selection, toast, refresh]);

  // Attach a recommended/connected integration to this agent: redeploy with the
  // server in the selection so its generated wrapper goes live. With
  // selection == null ("all connected") a freshly connected server still needs a
  // redeploy to get a wrapper, so we redeploy with null rather than no-op.
  const handleAttach = async (serverName: string) => {
    if (attaching) return;
    setAttaching(serverName);
    try {
      const merged: ProjectSelection | undefined =
        selection == null
          ? undefined
          : { servers: [...selection.servers.filter((s) => s.name !== serverName), { name: serverName, all_tools: true }] };
      await deploy(project.name ?? projectId, subdomainOfDeployment(project), projectId, merged);
      toast.success(`Attached ${serverName} — redeploying.`);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't attach the integration"));
    } finally {
      setAttaching(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Integrations</CardTitle>
        <CardDescription>
          Connect the integrations this agent's playbook recommends, then choose which servers and tools it exposes.
          Saving redeploys it — the URL and access tokens stay the same; the running sandbox is replaced.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : err ? (
          <ErrorState message={err} onRetry={() => void refresh()} />
        ) : (
          <>
            {/* What the stored playbook recommends — rendered even when nothing is
                connected, so a user who saved a playbook and closed the chat still
                learns what to set up. Hides itself when requires_tools is empty. */}
            <IntegrationsPanel
              needed={needed}
              servers={servers}
              selection={selection}
              attaching={attaching}
              onAttach={(n) => void handleAttach(n)}
              onAdd={() => setAddOpen(true)}
            />

            {connected.length > 0 ? (
              <>
                <ServerToolPicker servers={connected} initial={selection} onChange={setPicker} />

                {dropped.length > 0 && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                    <p className="font-medium">Saving will remove access to:</p>
                    <ul className="mt-1 list-inside list-disc space-y-0.5">
                      {dropped.map((d) => (
                        <li key={d} className="font-mono">
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 border-t pt-3">
                  <p className="text-xs text-muted-foreground">
                    {picker.isEmpty
                      ? "No integrations selected — code execution and persistent storage only."
                      : picker.selection
                        ? "A custom selection is active."
                        : "All connected integrations and their tools."}
                  </p>
                  <Button size="sm" onClick={() => void save()} disabled={saving}>
                    {saving ? <Spinner /> : "Save"}
                  </Button>
                </div>
              </>
            ) : needed.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-10 text-sm text-muted-foreground">
                <ServerCog className="h-6 w-6 opacity-50" />
                <span>
                  No integrations connected yet.{" "}
                  <button onClick={() => setAddOpen(true)} className="text-primary hover:underline">
                    Add one
                  </button>{" "}
                  to choose its tools here.
                </span>
              </div>
            ) : null}
          </>
        )}
      </CardContent>

      {addOpen && (
        <AddIntegrationDialog
          servers={servers}
          selection={selection}
          attaching={attaching}
          onAttach={(n) => void handleAttach(n)}
          onChanged={refresh}
          onClose={() => setAddOpen(false)}
        />
      )}
    </Card>
  );
}

function BackLink() {
  return (
    <Link to="/agents" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="h-4 w-4" /> Agents
    </Link>
  );
}

function CustomDomainsCard({ projectId, target }: { projectId: string; target: string }) {
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [wizard, setWizard] = useState<{ open: boolean; resume?: CustomDomain }>({ open: false });
  const [verifying, setVerifying] = useState<string | null>(null);
  const toast = useToast();
  const confirm = useConfirm();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelled = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const list = await listCustomDomains(projectId);
      if (!cancelled.current) {
        setDomains(list);
        setErr(null);
      }
    } catch (e) {
      if (!cancelled.current) setErr(errMsg(e, "Couldn't load custom domains"));
    } finally {
      if (!cancelled.current) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    cancelled.current = false;
    void refresh();
    return () => {
      cancelled.current = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [refresh]);

  // Poll while any domain is still settling so a verification flips without
  // a manual refresh (also picks up wizard-driven changes).
  useEffect(() => {
    if (!domains.some((d) => isSettling(d.status))) return;
    timer.current = setTimeout(() => void refresh(), 5000);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [domains, refresh]);

  const onRemove = useCallback(
    async (d: CustomDomain) => {
      const ok = await confirm({
        title: `Remove ${d.domain}?`,
        description: "Clients using this domain will stop working. You can add it again later.",
        confirmText: "Remove",
        cancelText: "Cancel",
      });
      if (!ok) return;
      try {
        await deleteCustomDomain(projectId, d.domain);
        setDomains((cur) => cur.filter((x) => x.domain !== d.domain));
        toast.success(`${d.domain} removed.`);
      } catch (e) {
        toast.error(errMsg(e, "Remove failed"));
      }
    },
    [projectId, confirm, toast],
  );

  const onReverify = useCallback(
    async (d: CustomDomain) => {
      setVerifying(d.domain);
      try {
        const updated = await verifyCustomDomain(projectId, d.domain);
        setDomains((cur) => cur.map((x) => (x.domain === d.domain ? updated : x)));
        if (isLive(updated.status)) toast.success(`${d.domain} is verified.`);
        else toast.error(`${d.domain} isn't verified yet — your DNS records may still be propagating.`);
      } catch (e) {
        toast.error(errMsg(e, "Re-verify failed"));
      } finally {
        setVerifying(null);
      }
    },
    [projectId, toast],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Custom domains</CardTitle>
          <CardDescription>
            Serve this agent’s <span className="font-mono">/mcp</span> on your own domain over HTTPS.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => setWizard({ open: true })}>
          <Plus className="h-4 w-4" /> Add domain
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : err ? (
          <ErrorState message={err} onRetry={() => void refresh()} />
        ) : domains.length === 0 ? (
          <button
            onClick={() => setWizard({ open: true })}
            className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed py-10 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/30"
          >
            <Globe className="h-6 w-6 opacity-50" />
            <span>
              No custom domains yet. <span className="font-medium text-foreground">Add one</span> to serve{" "}
              <span className="font-mono">/mcp</span> on your own hostname.
            </span>
          </button>
        ) : (
          <ul className="space-y-3">
            {domains.map((d) => (
              <DomainRow
                key={d.id}
                domain={d}
                verifying={verifying === d.domain}
                onContinue={() => setWizard({ open: true, resume: d })}
                onReverify={() => void onReverify(d)}
                onRemove={() => void onRemove(d)}
              />
            ))}
          </ul>
        )}
      </CardContent>

      <AddDomainWizard
        open={wizard.open}
        projectId={projectId}
        target={target}
        resume={wizard.resume}
        onClose={() => {
          setWizard({ open: false });
          void refresh();
        }}
        onChanged={(cd) => setDomains((cur) => [cd, ...cur.filter((x) => x.domain !== cd.domain)])}
      />
    </Card>
  );
}

function DomainRow({
  domain,
  verifying,
  onContinue,
  onReverify,
  onRemove,
}: {
  domain: CustomDomain;
  verifying: boolean;
  onContinue: () => void;
  onReverify: () => void;
  onRemove: () => void;
}) {
  const live = isLive(domain.status);
  return (
    <li className={cn("rounded-lg border transition-colors", verifying && "border-warning/40 bg-warning/10")}>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="truncate font-mono text-sm">{domain.domain}</span>
          {verifying ? (
            <Badge variant="outline" className="gap-1 border-warning/40 bg-warning/10 text-warning">
              <RefreshCw className="h-3 w-3 animate-spin" /> Checking DNS…
            </Badge>
          ) : (
            <StatusBadge status={domain.status} />
          )}
        </div>
        <div className="flex items-center gap-1">
          {!live && (
            <Button variant="outline" size="sm" onClick={onContinue} disabled={verifying}>
              Continue setup
            </Button>
          )}
          {live && (
            <a
              href={`https://${domain.domain}/mcp`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              Open <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Domain actions" disabled={verifying}>
                {verifying ? <Spinner /> : <MoreHorizontal className="h-4 w-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onReverify} disabled={verifying}>
                <RefreshCw /> Re-verify now
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onRemove} className="text-destructive focus:text-destructive">
                <Trash2 /> Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {live && (
        <div className="flex items-center gap-2 border-t px-4 py-2 font-mono text-xs">
          <span className="truncate">https://{domain.domain}/mcp</span>
          <CopyButton value={`https://${domain.domain}/mcp`} />
        </div>
      )}
      {domain.last_error && !live && (
        <p className="border-t px-4 py-2 text-xs text-orange-700">
          Not verified yet — double-check the DNS records below. Changes can take a while to propagate.
        </p>
      )}
    </li>
  );
}

// ── Add-domain wizard ────────────────────────────────────────────────────

type Provider = "cloudflare" | "manual";
type Step = 1 | 2 | 3;

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: "Domain" },
  { n: 2, label: "DNS records" },
  { n: 3, label: "Verify" },
];

function Stepper({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((s, i) => {
        const done = step > s.n;
        const active = step === s.n;
        return (
          <div key={s.n} className="flex items-center gap-2">
            <div
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full border text-xs font-semibold transition-colors",
                done && "border-success bg-success text-success-foreground",
                active && "border-primary bg-primary text-primary-foreground",
                !done && !active && "border-border bg-muted text-muted-foreground",
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : s.n}
            </div>
            <span className={cn("text-xs", active ? "font-medium text-foreground" : "text-muted-foreground")}>
              {s.label}
            </span>
            {i < STEPS.length - 1 && <div className="mx-1 h-px w-6 bg-border" />}
          </div>
        );
      })}
    </div>
  );
}

function AddDomainWizard({
  open,
  projectId,
  target,
  resume,
  onClose,
  onChanged,
}: {
  open: boolean;
  projectId: string;
  target: string;
  resume?: CustomDomain;
  onClose: () => void;
  onChanged: (cd: CustomDomain) => void;
}) {
  const [step, setStep] = useState<Step>(1);
  const [provider, setProvider] = useState<Provider>("cloudflare");
  const [domainInput, setDomainInput] = useState("");
  const [cd, setCd] = useState<CustomDomain | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // (Re)initialise whenever the dialog opens — fresh, or resuming a pending
  // binding straight at the DNS step.
  useEffect(() => {
    if (!open) return;
    if (resume) {
      setCd(resume);
      setDomainInput(resume.domain);
      setStep(isSettling(resume.status) || resume.status === "degraded" || resume.status === "failed" ? 2 : 3);
    } else {
      setCd(null);
      setDomainInput("");
      setStep(1);
    }
    setProvider("cloudflare");
    setErr("");
  }, [open, resume]);

  async function createDomain() {
    const d = domainInput.trim().toLowerCase().replace(/\.$/, "");
    if (!d) return;
    setBusy(true);
    setErr("");
    try {
      const created = await addCustomDomain(projectId, d);
      setCd(created);
      onChanged(created);
      setStep(2);
    } catch (e) {
      if (e instanceof APIError && e.status === 409) {
        setErr("That domain is already added — find it in the list to continue its setup.");
      } else {
        setErr(errMsg(e, "Couldn't add domain"));
      }
    } finally {
      setBusy(false);
    }
  }

  const runVerify = useCallback(async () => {
    if (!cd) return;
    setBusy(true);
    setErr("");
    try {
      const updated = await verifyCustomDomain(projectId, cd.domain);
      setCd(updated);
      onChanged(updated);
    } catch (e) {
      setErr(errMsg(e, "Verification check failed"));
    } finally {
      setBusy(false);
    }
  }, [cd, projectId, onChanged]);

  // Live auto-poll while the verify step is showing and the domain is still
  // settling — so it flips to "live" on its own without the user clicking.
  useEffect(() => {
    if (!open || step !== 3 || !cd || !isSettling(cd.status) || busy) return;
    const t = setTimeout(() => void runVerify(), 5000);
    return () => clearTimeout(t);
  }, [open, step, cd, busy, runVerify]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add a custom domain</DialogTitle>
          <DialogDescription>Point your own hostname at this agent’s MCP endpoint.</DialogDescription>
        </DialogHeader>

        <div className="border-y py-3">
          <Stepper step={step} />
        </div>

        {step === 1 && (
          <StepDomain
            value={domainInput}
            target={target}
            err={err}
            onChange={setDomainInput}
            onSubmit={() => void createDomain()}
          />
        )}
        {step === 2 && cd && (
          <StepDNS provider={provider} setProvider={setProvider} cd={cd} />
        )}
        {step === 3 && cd && <StepVerify cd={cd} err={err} />}

        <WizardFooter
          step={step}
          busy={busy}
          canContinue={step === 1 ? DOMAIN_RE.test(domainInput.trim()) : true}
          cd={cd}
          onBack={() => setStep((s) => (s > 1 ? ((s - 1) as Step) : s))}
          onNext={() => {
            if (step === 1) void createDomain();
            else if (step === 2) {
              setStep(3);
              void runVerify();
            } else void runVerify();
          }}
          onRecheck={() => void runVerify()}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

function StepDomain({
  value,
  target,
  err,
  onChange,
  onSubmit,
}: {
  value: string;
  target: string;
  err: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}) {
  const invalid = value.trim().length > 0 && !DOMAIN_RE.test(value.trim());
  return (
    <div className="space-y-2 py-1">
      <Label htmlFor="cd-domain">Your domain</Label>
      <Input
        id="cd-domain"
        placeholder="mcp.yourcompany.com"
        value={value}
        autoFocus
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !invalid) onSubmit();
        }}
      />
      {invalid && <p className="text-xs text-destructive">Enter a full domain like mcp.yourcompany.com</p>}
      <p className="text-xs text-muted-foreground">
        A subdomain you control{target ? <> — it will point at <span className="font-mono">{target}</span></> : null}.
        Next we'll show the exact DNS records to add.
      </p>
      {err && <p className="text-sm text-destructive">{err}</p>}
    </div>
  );
}

function StepDNS({
  provider,
  setProvider,
  cd,
}: {
  provider: Provider;
  setProvider: (p: Provider) => void;
  cd: CustomDomain;
}) {
  return (
    <div className="space-y-4 py-1">
      <div>
        <p className="mb-2 text-sm font-medium">Where is your DNS hosted?</p>
        <div className="grid grid-cols-2 gap-2">
          <ProviderTile
            active={provider === "cloudflare"}
            icon={Cloud}
            title="Cloudflare"
            subtitle="Fast guided setup"
            onClick={() => setProvider("cloudflare")}
          />
          <ProviderTile
            active={provider === "manual"}
            icon={ServerCog}
            title="Other provider"
            subtitle="Route 53, Namecheap, …"
            onClick={() => setProvider("manual")}
          />
        </div>
      </div>

      {provider === "cloudflare" ? (
        <Steps
          items={[
            <>
              Open your{" "}
              <a
                href="https://dash.cloudflare.com/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
              >
                Cloudflare dashboard <ExternalLink className="h-3 w-3" />
              </a>{" "}
              → your domain → <span className="font-medium">DNS → Records</span>.
            </>,
            <>Add the two records below (use “Add record”).</>,
            <>
              On the {cd.dns_records?.routing.type === "A" ? "A" : "CNAME"} record, set{" "}
              <span className="font-medium">Proxy status</span> to{" "}
              <span className="font-medium">DNS only (grey cloud)</span> — see the note.
            </>,
          ]}
        />
      ) : (
        <Steps
          items={[
            <>Open your DNS provider's control panel for this domain.</>,
            <>Add the two records below exactly as shown.</>,
            <>
              Remove any conflicting <span className="font-mono">A</span>/<span className="font-mono">AAAA</span>/
              <span className="font-mono">CNAME</span> on this hostname, plus any stale{" "}
              <span className="font-mono">_acme-challenge</span> TXT.
            </>,
          ]}
        />
      )}

      {cd.dns_records && (
        <div className="space-y-2">
          <DNSRow record={cd.dns_records.routing} />
          <DNSRow record={cd.dns_records.txt} />
        </div>
      )}

      {provider === "cloudflare" && (
        <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <span className="font-medium">Proxy must be off.</span> Cloudflare's orange-cloud proxy breaks certificate
            issuance and double-proxies your endpoint. Click the cloud icon so it's grey (“DNS only”).
          </p>
        </div>
      )}
    </div>
  );
}

function StepVerify({ cd, err }: { cd: CustomDomain; err: string }) {
  const live = isLive(cd.status);
  const checking = isSettling(cd.status);
  return (
    <div className="space-y-3 py-4 text-center">
      <div className="flex justify-center">
        {live ? (
          <CheckCircle2 className="h-10 w-10 text-emerald-500" />
        ) : checking ? (
          <RefreshCw className="h-10 w-10 animate-spin text-amber-500" />
        ) : (
          <AlertTriangle className="h-10 w-10 text-orange-500" />
        )}
      </div>
      {live ? (
        <>
          <p className="font-medium">{cd.domain} is live 🎉</p>
          <div className="mx-auto flex max-w-sm items-center justify-center gap-2 rounded-md border bg-muted/30 px-3 py-2 font-mono text-xs">
            <span className="truncate">https://{cd.domain}/mcp</span>
            <CopyButton value={`https://${cd.domain}/mcp`} />
          </div>
        </>
      ) : checking ? (
        <>
          <p className="font-medium">Checking DNS…</p>
          <p className="text-sm text-muted-foreground">
            DNS can take a few minutes to propagate. This keeps checking automatically — you can close this and come
            back; it'll finish on its own.
          </p>
        </>
      ) : (
        <>
          <p className="font-medium text-orange-700">Not verified yet</p>
          <p className="text-sm text-muted-foreground">Records not found. Double-check them and try again — DNS changes can take a while to propagate.</p>
        </>
      )}
      {err && <p className="text-sm text-destructive">{err}</p>}
    </div>
  );
}

function WizardFooter({
  step,
  busy,
  canContinue,
  cd,
  onBack,
  onNext,
  onRecheck,
  onClose,
}: {
  step: Step;
  busy: boolean;
  canContinue: boolean;
  cd: CustomDomain | null;
  onBack: () => void;
  onNext: () => void;
  onRecheck: () => void;
  onClose: () => void;
}) {
  return (
    <DialogFooter className="gap-2 sm:justify-between">
      <div>
        {step > 1 && (
          <Button variant="ghost" onClick={onBack} disabled={busy}>
            Back
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        {step === 1 && (
          <Button onClick={onNext} disabled={busy || !canContinue}>
            {busy && <Spinner />} Continue <ArrowRight className="h-4 w-4" />
          </Button>
        )}
        {step === 2 && (
          <Button onClick={onNext} disabled={busy}>
            I've added the records <ArrowRight className="h-4 w-4" />
          </Button>
        )}
        {step === 3 && (
          <>
            {cd && !isLive(cd.status) && (
              <Button variant="outline" onClick={onRecheck} disabled={busy}>
                {busy ? <Spinner /> : <RefreshCw className="h-4 w-4" />} Check again
              </Button>
            )}
            <Button onClick={onClose}>{cd && isLive(cd.status) ? "Done" : "Close"}</Button>
          </>
        )}
      </div>
    </DialogFooter>
  );
}

function ProviderTile({
  active,
  icon: Icon,
  title,
  subtitle,
  onClick,
}: {
  active: boolean;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-lg border p-3 text-left transition-colors",
        active ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/40",
      )}
    >
      <Icon className={cn("h-5 w-5", active ? "text-primary" : "text-muted-foreground")} />
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
    </button>
  );
}

function Steps({ items }: { items: React.ReactNode[] }) {
  return (
    <ol className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-sm">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
            {i + 1}
          </span>
          <span className="text-muted-foreground">{it}</span>
        </li>
      ))}
    </ol>
  );
}

function DNSRow({ record }: { record: CustomDomainRecord }) {
  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5 text-xs">
      <Badge variant="secondary" className="font-mono">
        {record.type}
      </Badge>
      <div className="min-w-0 font-mono">
        <div className="flex items-center gap-1">
          <span className="w-12 shrink-0 text-muted-foreground">name</span>
          <span className="truncate">{record.name}</span>
          <CopyButton value={record.name} />
        </div>
        <div className="flex items-center gap-1">
          <span className="w-12 shrink-0 text-muted-foreground">value</span>
          <span className="truncate">{record.value}</span>
          <CopyButton value={record.value} />
        </div>
      </div>
    </div>
  );
}
